import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pencil, Clock, Loader2, Plus, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AvailabilityCardProps {
  availabilityStatus: string;
  isEditing: boolean;
  isSaving: boolean;
  onEditClick: () => void;
  onCancel: () => void;
  onSave: () => void;
  onStatusChange: (value: string) => void;
}

export function AvailabilityCard({
  availabilityStatus,
  isEditing,
  isSaving,
  onEditClick,
  onCancel,
  onSave,
  onStatusChange,
}: AvailabilityCardProps) {
  const isEmpty = !availabilityStatus && !isEditing;
  const [error, setError] = useState<string>('');
  const previousValueRef = useRef<string>('');
  const prevIsEditingRef = useRef<boolean>(false);

  // Track previous value when editing starts
  useEffect(() => {
    // Only capture the previous value when transitioning from non-editing to editing
    if (isEditing && !prevIsEditingRef.current) {
      previousValueRef.current = availabilityStatus;
      setError('');
    }
    prevIsEditingRef.current = isEditing;
  }, [isEditing, availabilityStatus]);

  // Validation function
  const validate = (value: string): boolean => {
    if (!value || value.trim() === '') {
      setError('Please select an availability status');
      return false;
    }
    setError('');
    return true;
  };

  // Handle cancel - restore previous value
  const handleCancel = () => {
    onStatusChange(previousValueRef.current);
    setError('');
    onCancel();
  };

  // Handle save - validate before saving
  const handleSave = () => {
    if (validate(availabilityStatus)) {
      onSave();
    }
  };

  // Clear error when value changes
  const handleStatusChange = (value: string) => {
    onStatusChange(value);
    if (error) {
      setError('');
    }
  };

  return (
    <div className={cn(
      "rounded-xl sm:rounded-[20px] p-4 sm:p-6 shadow-sm",
      isEmpty
        ? "bg-amber-50/50 border-2 border-dashed border-amber-500/30"
        : "bg-white border border-slate-200"
    )}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Clock className="h-5 w-5 text-slate-700" />
          <h3 className="text-lg font-semibold text-slate-900">Availability</h3>
          {isEmpty && (
            <Badge className="bg-amber-500 text-white rounded-[48px] px-3 sm:px-4 py-1 text-xs">
              Incomplete
            </Badge>
          )}
        </div>
        {!isEditing && !isEmpty && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onEditClick}
            className="text-[#4669ed]"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="availability" className="text-sm">Availability Status</Label>
            <Select
              value={availabilityStatus || undefined}
              onValueChange={(value) => {
                handleStatusChange(value);
              }}
            >
              <SelectTrigger
                id="availability"
                className={cn(
                  "h-10 w-full",
                  error 
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500" 
                    : "border-slate-200 focus:border-[#4669ed] focus:ring-[#4669ed]"
                )}
              >
                <SelectValue placeholder="Select availability" />
              </SelectTrigger>
              <SelectContent className="rounded-xl border-2 border-slate-200">
                <SelectItem value="available" className="focus:bg-slate-200 focus:text-slate-900">Available</SelectItem>
                <SelectItem value="busy" className="focus:bg-slate-200 focus:text-slate-900">Busy</SelectItem>
                <SelectItem value="part-time" className="focus:bg-slate-200 focus:text-slate-900">Part-time</SelectItem>
                <SelectItem value="unavailable" className="focus:bg-slate-200 focus:text-slate-900">Unavailable</SelectItem>
              </SelectContent>
            </Select>
            {error && (
              <p className="text-[10px] text-red-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {error}
              </p>
            )}
          </div>
          <div className="flex gap-2 pt-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCancel} 
              disabled={isSaving}
              className="flex-1 rounded-full"
            >
              Cancel
            </Button>
            <Button 
              size="sm" 
              onClick={handleSave} 
              disabled={isSaving || !availabilityStatus || availabilityStatus.trim() === ''}
              className="flex-1 bg-[#4669ed] hover:bg-[#2b42a3] rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </div>
      ) : (
        <div>
          {availabilityStatus ? (
            <Badge className={cn(
              "rounded-full px-3 py-1.5 text-xs font-semibold",
              availabilityStatus === 'available' ? 'bg-emerald-100 text-emerald-800' :
              availabilityStatus === 'busy' ? 'bg-orange-100 text-orange-800' :
              availabilityStatus === 'part-time' ? 'bg-blue-100 text-blue-800' :
              'bg-slate-100 text-slate-800'
            )}>
              {availabilityStatus === 'available' ? 'Available' :
               availabilityStatus === 'busy' ? 'Busy' :
               availabilityStatus === 'part-time' ? 'Part-time' :
               'Unavailable'}
            </Badge>
          ) : (
            <div className="flex flex-col items-center py-4">
              {/* Icon Circle */}
              <div className="w-20 h-20 rounded-[40px] bg-slate-200 flex items-center justify-center mb-6">
                <Clock className="w-6 h-6 text-slate-500" />
              </div>

              {/* Title */}
              <h3 className="text-xl font-normal text-slate-900 text-center mb-2">
                Add Your Availability
              </h3>

              {/* Description */}
              <p className="text-sm text-slate-500 text-center max-w-[280px] leading-relaxed mb-6">
                Let clients know when you're available for new projects.
              </p>

              {/* CTA Button */}
              <Button
                onClick={onEditClick}
                className="bg-[#4669ed] hover:bg-[#2b42a3] rounded-[48px] px-6"
              >
                <Plus className="w-5 h-5" />
                Set Availability
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
